<template>
  <div class="n-tooltip__content" ref="messageEl" :class="tooltipClasses" :aria-hidden="hidden">
    <VNodeRenderer :nodes="content" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, PropType, onBeforeUnmount } from 'vue';
import VNodeRenderer from './vnode-renderer.vue';

defineOptions({
  name: 'n-tooltip-content',
});

const props = defineProps({
  content: {
    type: [String, Object],
    default: '',
  },
  position: {
    type: String as PropType<'top' | 'bottom' | 'left' | 'right'>,
    default: 'top',
  },
  iconOffsetPx: {
    type: Number,
    default: 0,
  },
  wide: {
    type: Boolean,
    default: false,
  },
});

const hidden = ref(true);
const messageEl = ref<HTMLDivElement | null>(null);
const offset = ref(5);

function calculatePosition(hostPosition: DOMRect) {
  if (!messageEl.value) return;

  const tooltipPosition = messageEl.value.getBoundingClientRect();
  let top = 0;
  let left = 0;

  const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

  if (props.position === 'top') {
    top = hostPosition.top - tooltipPosition.height - offset.value - props.iconOffsetPx; // Shift up by icon offset
    left = hostPosition.left + hostPosition.width / 2 - tooltipPosition.width / 2;
  } else if (props.position === 'bottom') {
    top = hostPosition.bottom + offset.value + props.iconOffsetPx; // Shift down by icon offset
    left = hostPosition.left + hostPosition.width / 2 - tooltipPosition.width / 2;
  } else if (props.position === 'left') {
    top = hostPosition.top + (hostPosition.height - tooltipPosition.height) / 2 - props.iconOffsetPx;
    left = Math.max(hostPosition.left - tooltipPosition.width - offset.value, 0);
  } else if (props.position === 'right') {
    top = hostPosition.top + (hostPosition.height - tooltipPosition.height) / 2 - props.iconOffsetPx;
    left = hostPosition.right + offset.value;
  }

  if (messageEl.value) {
    messageEl.value.style.left = `${left}px`;
    messageEl.value.style.top = `${top + scrollPosition}px`;
  }
}

// Computed for tooltip classes
const tooltipClasses = computed(() => {
  return {
    'n-tooltip__content--top': props.position === 'top',
    'n-tooltip__content--hidden': hidden.value,
    'n-tooltip__content--bottom': props.position === 'bottom',
    'n-tooltip__content--left': props.position === 'left',
    'n-tooltip__content--right': props.position === 'right',
    'n-tooltip__content--wide': props.wide,
  };
});

const remove = () => {
  if (messageEl.value && messageEl.value.parentNode) {
    messageEl.value.parentNode.removeChild(messageEl.value);
  }
};

onBeforeUnmount(() => {
  const parent = messageEl.value?.parentNode;
  if (parent) {
    parent.removeChild(messageEl.value!);
  }
});

defineExpose({
  hidden,
  position: props.position,
  calculatePosition,
  remove,
  content: props.content,
});
</script>
<style lang="scss" scoped>
.n-tooltip__content {
  position: absolute;
  background: black;
  color: white;
  border-radius: 5px;
  padding: 5px 10px 4px;
  max-width: 300px;
  font-size: 0.75rem;
  top: 0;
  left: 0;
  z-index: 99;
  &--wide {
    max-width: 800px;
  }
  &--hidden {
    visibility: hidden;
    z-index: -1;
  }
  &::after {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }
  &--top {
    &::after {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 10px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  &--bottom {
    &::after {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 10px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  &--right {
    &::after {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 10px);
      margin-left: 0;
      margin-right: 0;
    }
  }
  &--left {
    &::after {
      border-width: 5px 0 5px 5px;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 10px);
      margin-left: 0;
      margin-right: 0;
    }
  }
}
</style>
